import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Save } from "lucide-react";
import { calcularEstado } from "../../lib/leadEstado";
import LeadFormFields, { FormField } from "./LeadFormFields";
import LeadCallsEditor from "./LeadCallsEditor";
import LeadOptionsEditor from "./LeadOptionsEditor";
import LeadSourceDetails from "./LeadSourceBadge";

const inputClass =
  "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none";

const makeDefaultLlamadas = () => [
  { fecha: "", resultado: "", realizada: false },
  { fecha: "", resultado: "", realizada: false },
  { fecha: "", resultado: "", realizada: false },
];

const DEFAULT_LEAD = {
  estado: "",
  nombre: "",
  fecha_contacto: new Date().toISOString().split("T")[0],
  resultado: "",
  fecha_resultado: "",
  canal: "",
  servicio: "",
  sesion_prueba: false,
  telefono: "",
  email: "",
  como_conocio: "",
  observaciones: "",
  llamadas: [],
  seguimiento: "",
};

function normalizeLlamadasFromLead(lead) {
  let llamadas = lead.llamadas;
  if (!llamadas || llamadas.length === 0) {
    llamadas = [
      { fecha: "", resultado: lead.llamada_1 || "" },
      { fecha: "", resultado: lead.llamada_2 || "" },
      { fecha: "", resultado: lead.llamada_3 || "" },
    ].filter(l => l.resultado);
    if (llamadas.length === 0) return makeDefaultLlamadas();
    while (llamadas.length < 3) {
      llamadas.push({ fecha: "", resultado: "", realizada: false });
    }
  }
  return llamadas.map(l => ({ ...l, realizada: !!l.realizada }));
}

export default function LeadForm({
  lead,
  leads = [],
  onSave,
  onClose,
  saving,
  conocioOptions = [],
  canalOptions = [],
  servicioOptions = [],
  onSaveOptions,
  savingOptions,
}) {
  const [form, setForm] = useState(() => ({ ...DEFAULT_LEAD, llamadas: makeDefaultLlamadas() }));
  const [visible, setVisible] = useState(false);
  const [editingOptions, setEditingOptions] = useState(null);
  const isEditing = !!lead?.id;

  const allOptions = {
    canal: canalOptions,
    servicio: servicioOptions,
    conocio: conocioOptions,
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    if (lead) {
      setForm({
        ...DEFAULT_LEAD,
        ...lead,
        fecha_contacto: lead.fecha_contacto || DEFAULT_LEAD.fecha_contacto,
        fecha_resultado: lead.fecha_resultado || "",
        llamadas: normalizeLlamadasFromLead(lead),
      });
    } else {
      setForm({ ...DEFAULT_LEAD, llamadas: makeDefaultLlamadas() });
    }
  }, [lead]);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const update = (field, value) => {
    setForm((f) => {
      const newForm = { ...f, [field]: value };
      if (field === "resultado" && value && !f.fecha_resultado) {
        newForm.fecha_resultado = new Date().toISOString().split("T")[0];
      }
      return newForm;
    });
  };

  const updateLlamada = (idx, field, value) => {
    setForm((f) => {
      const llamadas = [...f.llamadas];
      llamadas[idx] = { ...llamadas[idx], [field]: value };
      return { ...f, llamadas };
    });
  };

  const addLlamada = () => {
    setForm((f) => ({
      ...f,
      llamadas: [...f.llamadas, { fecha: "", resultado: "", realizada: false }],
    }));
  };

  const removeLlamada = (idx) => {
    if (form.llamadas.length <= 1) return;
    setForm((f) => ({ ...f, llamadas: f.llamadas.filter((_, i) => i !== idx) }));
  };

  const estadoCalculado = calcularEstado(form.resultado, form.fecha_resultado, form.fecha_contacto);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!form.nombre.trim()) return;
    onSave({ ...form, estado: estadoCalculado });
  };

  const handleSaveOptions = (type, items) => {
    onSaveOptions(type, items).then(() => setEditingOptions(null));
  };

  const formContent = (
    <div className="space-y-4">
      {isEditing && lead?.source === "public_form" && (
        <LeadSourceDetails
          source={lead.source}
          metadata={lead.source_metadata}
          submittedAt={lead.submitted_at}
        />
      )}

      <LeadFormFields
        form={form}
        isEditing={isEditing}
        estadoCalculado={estadoCalculado}
        update={update}
        conocioOptions={conocioOptions}
        canalOptions={canalOptions}
        servicioOptions={servicioOptions}
        onConfigureOptions={setEditingOptions}
      />

      {isEditing && (
        <>
          <LeadCallsEditor
            llamadas={form.llamadas}
            onUpdate={updateLlamada}
            onAdd={addLlamada}
            onRemove={removeLlamada}
          />

          <FormField label="Seguimiento (si no es cliente)">
            <textarea
              value={form.seguimiento}
              onChange={(e) => update("seguimiento", e.target.value)}
              rows={2}
              className={inputClass}
              placeholder="Notas de seguimiento..."
            />
          </FormField>
        </>
      )}
    </div>
  );

  const header = (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
      <span className="text-base font-semibold text-gray-900">
        {isEditing ? "Editar Lead" : "Nuevo Lead"}
      </span>
      <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
        <X size={18} className="text-gray-400" />
      </button>
    </div>
  );

  const footer = (
    <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
      <button onClick={close} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
        Cancelar
      </button>
      <button
        onClick={handleSubmit}
        disabled={saving || !form.nombre.trim()}
        className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {isEditing ? "Guardar" : "Crear"}
      </button>
    </div>
  );

  const panel = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-250 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={close}
      />

      {/* Desktop: right drawer */}
      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-xl
          flex-col bg-white shadow-2xl
          transition-transform duration-250 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {header}
        <div className="flex-1 px-6 py-6 overflow-y-auto">{formContent}</div>
        {footer}
      </div>

      {/* Mobile: full-screen sheet */}
      <div
        className={`
          md:hidden fixed inset-0 bg-white
          transition-transform duration-250 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
          flex flex-col
        `}
      >
        {header}
        <div className="flex-1 px-5 py-4 overflow-y-auto">{formContent}</div>
        {footer}
      </div>

      {editingOptions && (
        <LeadOptionsEditor
          type={editingOptions}
          options={allOptions[editingOptions]}
          leads={leads}
          onSave={handleSaveOptions}
          onBack={() => setEditingOptions(null)}
          saving={savingOptions}
        />
      )}
    </div>
  );

  return createPortal(panel, document.body);
}
